'use client'

// Between you and him.
//
// The old tab was a stack of identical cream cards, each with a coloured
// pill and 300 characters of his diary. Sam, 25 Sep, looking at his own:
// "the mentions tab could be presented way better and more user friendly
// ... do something totally new here and clearer to understand."
//
// Three things were wrong and only one of them was decoration.
//
// 1. The words were his machinery, not his voice. "Under the note
//    sleepyCrow64 left me, to sleepyCrow64:" is a diary keeping track of
//    itself. Fixed at the door: lib/ui/mentions.js turns a row into a
//    headline said from YOUR side and his actual words, separately.
// 2. One entry held three people's replies and the page printed all of
//    them, truncated, so your own sentence was the one cut in half.
//    Fixed in /api/mentions and in Board.js, which now writes the parts
//    as data.
// 3. The pill was the loudest thing on the card and it never said
//    anything the sentence did not. Gone.
//
// What replaces it reads as correspondence rather than a log: his words
// first and large, what it was about underneath them in small type, your
// own words above when the entry remembers them, and the days named. The
// things that are news rather than speech drop to a single line with a
// dot, because they are not him talking to you and should not look as
// though they are.

import { useEffect, useMemo, useState } from 'react'
import { authFetch } from '../auth/useUser'
import { SkeletonCard } from '../auth/AuthSlot'
import { Card } from './Drawer'
import { FeedItem } from './OfferingsDock'
import { kawaii, ui } from '../../lib/ui/theme'
import { BIRD_NAME } from '../../lib/world/identity'
import { readMention, byDay, GROUPS, groupCounts } from '../../lib/ui/mentions'

const clock = (ts) => new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

/** "3 September", for the day he first noticed you. */
const longDay = (ts) => new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })

// ── The head of the page ────────────────────────────────────────────────
//
// One paragraph of plain fact instead of the old explainer card, which
// spent five lines saying that mentions are mentions. Everything here is
// counted from what is actually on the page, or read off the relationship
// the sim keeps (the `personal` block, which the route has been fetching
// and nobody was rendering).

function Summary({ rows, personal, seenAt }) {
  const rel = personal?.relationships?.[0] || null
  const shaped = (personal?.influences || []).filter((i) => i.stage === 'acted_on' || i.stage === 'changed_something')
  const fresh = rows.filter((r) => r.ts > (seenAt || 0)).length
  const spoke = rows.filter((r) => r.said).length

  return (
    <Card>
      <p style={{ color: kawaii.ink, fontSize: 16, fontWeight: 800, lineHeight: 1.35 }}>
        {rows.length === 0
          ? `${BIRD_NAME} has not said your name yet.`
          : rel?.firstAt
            ? `He has known you since ${longDay(rel.firstAt)}.`
            : `${BIRD_NAME} has said your name ${rows.length === 1 ? 'once' : `${rows.length} times`}.`}
      </p>
      <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, lineHeight: 1.6, marginTop: 6 }}>
        {rows.length === 0
          ? 'Leave him something at the shrine, or do something in his town where he can find it. Everything of his life that carries your name turns up here.'
          : [
            `${rows.length === 1 ? 'one time' : `${rows.length} times`} he has said your name`,
            spoke ? `${spoke === 1 ? 'once' : `${spoke} of them`} in his own words` : null,
            shaped.length ? `${shaped.length} ${shaped.length === 1 ? 'thing' : 'things'} he did came from you` : null,
          ].filter(Boolean).join(', ') + '.'}
      </p>
      {fresh > 0 && (
        <p style={{ color: kawaii.ink, fontSize: 13, fontWeight: 800, lineHeight: 1.5, marginTop: 8 }}>
          {fresh === 1 ? 'one of them is new since you last looked.' : `${fresh} of them are new since you last looked.`}
        </p>
      )}
      {/* What you left that changed something. This is the payoff of the
          whole mechanic and it had no surface at all: the route fetched
          it and the page dropped it on the floor. */}
      {shaped.slice(0, 3).map((i) => (
        <p key={i.id} style={{ color: kawaii.ink, fontSize: 13, fontWeight: 600, lineHeight: 1.55, marginTop: 8 }}>
          <span style={{ color: kawaii.inkSoft }}>from you: </span>
          {i.consequence || i.latest}
        </p>
      ))}
    </Card>
  )
}

// ── The filters ─────────────────────────────────────────────────────────
//
// Only the ones with something behind them are drawn. A chip that empties
// the list is a chip that lies, and five chips on somebody's third day
// would all be empty but one.

function Chip({ on, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 13px',
        borderRadius: 999,
        background: on ? kawaii.ink : kawaii.cream,
        color: on ? kawaii.cream : kawaii.inkSoft,
        border: kawaii.outlineThin,
        boxShadow: on ? 'none' : kawaii.dropSm,
        fontFamily: ui.font,
        fontSize: 12.5,
        fontWeight: 800,
        cursor: on ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  )
}

// ── The two registers ───────────────────────────────────────────────────

/** The quote marks, drawn. Nothing typographic, nothing from a font. */
function QuoteMark() {
  return (
    <svg width="17" height="13" viewBox="0 0 17 13" aria-hidden="true" style={{ flex: 'none', marginTop: 3 }}>
      <path
        fill={kawaii.ink}
        opacity="0.3"
        d="M6.6 0C3 1.3.8 4.2.8 7.7c0 3 1.6 4.9 3.9 4.9 2 0 3.5-1.5 3.5-3.4 0-1.9-1.3-3.3-3.1-3.3-.4 0-.8 0-1 .2.4-1.7 1.9-3.3 3.8-4.2L6.6 0Zm8.7 0c-3.6 1.3-5.8 4.2-5.8 7.7 0 3 1.6 4.9 3.9 4.9 2 0 3.5-1.5 3.5-3.4 0-1.9-1.3-3.3-3.1-3.3-.4 0-.8 0-1 .2.4-1.7 1.9-3.3 3.8-4.2L15.3 0Z"
      />
    </svg>
  )
}

/**
 * He said this. His words carry the card; the headline drops underneath
 * them as an attribution, which is the whole inversion: the machinery
 * used to be the first thing you read.
 */
function Spoken({ row }) {
  return (
    <Card>
      {row.theirs && (
        <div
          style={{
            background: kawaii.creamDeep,
            border: kawaii.stitch,
            borderRadius: 12,
            padding: '7px 11px',
            marginBottom: 11,
          }}
        >
          <p style={{ color: kawaii.inkSoft, fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            what you left him
          </p>
          <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, marginTop: 2 }}>
            {row.theirs}
          </p>
        </div>
      )}
      <div style={{ display: 'flex', gap: 9 }}>
        <QuoteMark />
        <p style={{ color: kawaii.ink, fontSize: 15, fontWeight: 700, lineHeight: 1.55, minWidth: 0 }}>
          {row.said}
        </p>
      </div>
      <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, marginTop: 9 }}>
        {row.headline} <span style={{ opacity: 0.8 }}>{clock(row.ts)}</span>
      </p>
      {row.closing && (
        <p style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 600, lineHeight: 1.5, marginTop: 3, fontStyle: 'italic' }}>
          he has said his last word on that one.
        </p>
      )}
    </Card>
  )
}

/**
 * Something happened. Not a card: news about you is not him talking to
 * you, and eight identical cards is what made the old tab unreadable.
 */
function Happened({ row }) {
  return (
    <div style={{ display: 'flex', gap: 11, padding: '3px 2px 3px 4px' }}>
      <span
        aria-hidden="true"
        style={{
          width: 9, height: 9, borderRadius: 999, flex: 'none', marginTop: 6,
          background: kawaii.cream, border: kawaii.outlineThin,
        }}
      />
      <p style={{ color: kawaii.ink, fontSize: 13.5, fontWeight: 600, lineHeight: 1.55, minWidth: 0, flex: 1 }}>
        {row.headline}
        {row.aside ? <span style={{ color: kawaii.inkSoft }}> {row.aside}</span> : null}
      </p>
      <span style={{ color: kawaii.inkSoft, fontSize: 11.5, fontWeight: 700, flex: 'none', marginTop: 2 }}>
        {clock(row.ts)}
      </span>
    </div>
  )
}

function DayHeading({ children }) {
  return (
    <div className="flex items-center gap-3" style={{ paddingTop: 6 }}>
      <span style={{ color: kawaii.inkSoft, fontSize: 11.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', flex: 'none' }}>
        {children}
      </span>
      <span aria-hidden="true" style={{ flex: 1, height: 0, borderTop: kawaii.stitch }} />
    </div>
  )
}

// ── The pane ────────────────────────────────────────────────────────────

export function MentionsPane({ offerings = null, standing = null }) {
  const [data, setData] = useState(null)

  useEffect(() => {
    let dead = false
    authFetch('/api/mentions', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => {
        if (dead) return
        setData(d)
        // Now that they have been read, and not before: the payload
        // carries the old seenAt so the page can say which of these
        // landed since the last visit, and moving the clock first would
        // take that away every time.
        authFetch('/api/mentions', { method: 'POST' }).catch(() => {})
      })
      .catch(() => { if (!dead) setData({ error: 'could not reach his diary' }) })
    return () => { dead = true }
  }, [])

  return <MentionsView data={data} offerings={offerings} standing={standing} />
}

/**
 * The page itself, given the payload. Apart from the fetch so the whole
 * thing can be put in front of real rows without an account: every
 * specimen in test/mentions.test.js came off production, and the point
 * of the rebuild is what they look like, which a unit test cannot see.
 */
export function MentionsView({ data, offerings = null, standing = null }) {
  const [filter, setFilter] = useState('all')

  // One read per row, in one place, so nothing further down the page
  // touches a journal field by hand.
  const rows = useMemo(
    () => (data?.mentions || []).map((m) => readMention(m, { names: data?.me || [] })),
    [data],
  )
  const counts = useMemo(() => groupCounts(rows), [rows])
  const chips = GROUPS.filter((g) => counts[g.id] > 0)
  const shown = filter === 'all' ? rows : rows.filter((r) => r.group === filter)
  const days = useMemo(() => byDay(shown), [shown])

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 pt-1">
      {!data && (
        <>
          <SkeletonCard bars={[64, 88, 40]} />
          <SkeletonCard bars={[90, 72, 46]} />
          <SkeletonCard bars={[84, 60]} />
        </>
      )}

      {data?.error && (
        <Card><p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600 }}>{data.error}</p></Card>
      )}

      {data && !data.error && (
        <>
          <Summary rows={rows} personal={data.personal} seenAt={data.seenAt} />

          {chips.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Chip on={filter === 'all'} onClick={() => setFilter('all')}>everything</Chip>
              {chips.map((g) => (
                <Chip key={g.id} on={filter === g.id} onClick={() => setFilter(g.id)}>
                  {g.label}
                </Chip>
              ))}
            </div>
          )}

          {days.map((day) => (
            <div key={day.label} className="space-y-3">
              <DayHeading>{day.label}</DayHeading>
              {day.rows.map((row, i) => (
                row.said
                  ? <Spoken key={`${row.ts}_${i}`} row={row} />
                  : <Happened key={`${row.ts}_${i}`} row={row} />
              ))}
            </div>
          ))}

          {/* Your side of it. An offering he has not judged yet is the
              other half of this page, and checking two places for one
              conversation is what folding the tabs together ended. */}
          {offerings && (
            <div className="space-y-3">
              <DayHeading>what you have left him</DayHeading>
              {offerings.length === 0
                ? (
                  <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, fontStyle: 'italic', lineHeight: 1.55, padding: '2px 4px' }}>
                    you have not left him anything yet.
                  </p>
                )
                : offerings.map((o) => (
                  <FeedItem key={o.id} s={o} showHandle={false} mine standing={standing?.accepted ?? 0} />
                ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MentionsPane
